import type { Ref, SubmitEvent } from "react";

import { useTranslation } from "react-i18next";

import { useCreateAccountAbuseReport } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { CardSubtitleHeader, CardTextContent } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { Textarea } from "@/components/Textarea";

export function ReportAccountModal({
  ref,
  accountId,
  onClose,
}: {
  accountId: string;
  ref: Ref<HTMLDialogElement>;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const createReport = useCreateAccountAbuseReport({
    mutation: { onSuccess: () => onClose?.() },
  });

  function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    const form = new FormData(e.currentTarget);
    const description = form.get("description")! as string;
    createReport.mutate({ accountId, data: { description } });
  }

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="flex w-lg flex-col">
      <CardSubtitleHeader>{t("report-abuse")}</CardSubtitleHeader>
      <CardTextContent>
        <Form error={createReport.error} className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <FormField title={t("describe-the-problem")}>
            <Textarea name="description" required placeholder={t("report-account-placeholder")} />
          </FormField>
          <ActionBox>
            <Button attention="danger" type="button" onClick={onClose} disabled={createReport.isPending}>
              {t("cancel")}
            </Button>
            <Button attention="submit" type="submit" disabled={createReport.isPending}>
              {t("submit")}
            </Button>
          </ActionBox>
        </Form>
      </CardTextContent>
    </Dialog>
  );
}
